import Button from 'admin/components/atoms/Buttons';
import CheckBox from 'admin/components/atoms/CheckBox';
import Headings from 'admin/components/atoms/Headings';
import moment, { Moment } from 'moment';
import React, { useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import {
  createClearingZenginFile,
  ZenginExcluded,
  ZenginFileResult,
  ZenginWarning,
} from '~/javascripts/admin/actions/clearing';
import DateTime from '../../../atoms/DateTime';

type PrivateMoney = {
  id: string;
  name: string;
};

// 文字不正エラーでサーバが返す明細。運用者が直す対象を特定できるようにするためのもの。
type InvalidField = {
  organization_code: string;
  organization_name: string;
  field: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  privateMoneys: PrivateMoney[];
  // 一覧の絞り込みをそのまま引き継ぐ。プレビューと一覧で対象がずれないようにするため。
  defaultPrivateMoneyIds: string[];
  closingDateFrom?: string;
  closingDateTo?: string;
  claimedOrganizationCode?: string;
};

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '900px',
    width: '80vw',
    maxHeight: '85vh',
    overflow: 'auto' as const,
  },
};

const Section = styled.div`
  margin-bottom: 24px;
`;

const MoneyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
`;

const MoneyLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-weight: normal;
  cursor: pointer;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const Summary = styled.div`
  display: flex;
  gap: 32px;
  padding: 12px 16px;
  background: #f5f7f9;
  border-radius: 4px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: bold;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 6px 8px;
    border-bottom: 1px solid #e5e5e5;
    text-align: left;
    font-size: 13px;
  }
  th {
    background: #fafafa;
  }
  td.amount {
    text-align: right;
  }
`;

const Notice = styled.div<{ level: 'warning' | 'error' }>`
  padding: 12px 16px;
  border-radius: 4px;
  background: ${({ level }) => (level === 'error' ? '#fdecea' : '#fff8e1')};
  border: 1px solid ${({ level }) => (level === 'error' ? '#f5c6cb' : '#ffe082')};
  font-size: 13px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const MerchantCode = styled.div`
  color: #888;
  font-size: 11px;
`;

const ErrorText = styled.div`
  color: #d9534f;
  font-size: 13px;
  margin-bottom: 12px;
`;

const yen = (amount: number) => `${amount.toLocaleString()} 円`;

const openInNewTab = (url: string) => {
  const element = document.createElement('a');
  element.href = url;
  element.target = '_blank';
  element.rel = 'noopener noreferrer';
  element.download = '';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

// 警告の文言。運用者が「誰の」「何を」直せばよいかが分かる形にする。
const warningText = (warning: ZenginWarning) => {
  const who = warning.organization_name
    ? `${warning.organization_name}（${warning.organization_code}）`
    : (warning.organization_code ?? '');
  switch (warning.type) {
    case 'bank_master_unresolved':
      return `${who}: 銀行マスタで ${FIELD_LABELS[warning.field ?? ''] ?? warning.field ?? ''} を解決できませんでした（銀行番号 ${
        warning.bank_code ?? '不明'
      }）。登録値をそのまま使用します`;
    case 'field_sanitized':
      return `${who}: ${
        FIELD_LABELS[warning.field ?? ''] ?? warning.field ?? ''
      } に全銀で使えない文字が含まれていたため整形しました`;
    default:
      return JSON.stringify(warning);
  }
};

// 一覧に出す件数の上限。加盟店数が多い発行体では警告・除外が数百件になり、
// 全部並べるとモーダルが読めなくなるため、残りは件数だけ伝える。
const MAX_LISTED = 10;

const withOverflowNote = (texts: string[]) =>
  texts.length > MAX_LISTED
    ? [...texts.slice(0, MAX_LISTED), `他 ${texts.length - MAX_LISTED} 件`]
    : texts;

// unsettled_in_other_batch は精算データ1件につき1つ返るため、同じ文言が件数分並んでしまう。
// 運用者が知りたいのは「何件あるか」なので集約して1行にする。
const warningLines = (warnings: ZenginWarning[]) => {
  const unsettled = warnings.filter((w) => w.type === 'unsettled_in_other_batch');
  const others = warnings.filter((w) => w.type !== 'unsettled_in_other_batch');
  return [
    ...(unsettled.length > 0
      ? [
          `未精算のまま別の振込データに含まれている精算が ${unsettled.length} 件あります。前回分を精算済にし忘れている可能性があります`,
        ]
      : []),
    ...withOverflowNote(others.map(warningText)),
  ];
};

const excludedText = (excluded: ZenginExcluded) =>
  `${excluded.claimed_organization.name}（${excluded.claimed_organization.code}）`;

const FIELD_LABELS: Record<string, string> = {
  bank_account_holder_name: '受取人名',
  bank_originator_name: '委託者名',
  bank_code: '銀行番号',
  bank_branch_code: '支店番号',
  bank_account: '口座番号',
};

const invalidFieldText = (invalid: InvalidField) =>
  `${invalid.organization_name}（${invalid.organization_code}）: ${
    FIELD_LABELS[invalid.field] ?? invalid.field
  }`;

const ZenginExportModal = ({
  isOpen,
  onClose,
  privateMoneys,
  defaultPrivateMoneyIds,
  closingDateFrom,
  closingDateTo,
  claimedOrganizationCode,
}: Props) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(defaultPrivateMoneyIds);
  const [transferDate, setTransferDate] = useState<Moment>(moment());
  const [preview, setPreview] = useState<ZenginFileResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // 文字不正エラーで返る「どの加盟店のどの項目か」の明細
  const [invalidFields, setInvalidFields] = useState<InvalidField[]>([]);

  const reset = () => {
    setPreview(null);
    setErrorMessage(null);
    setInvalidFields([]);
    setIsLoading(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const toggleMoney = (id: string) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const requestParams = (dryRun: boolean) => ({
    private_money_ids: selectedIds,
    scheduled_transfer_date: transferDate.format(),
    closing_date_from: closingDateFrom,
    closing_date_to: closingDateTo,
    claimed_organization_code: claimedOrganizationCode,
    dry_run: dryRun,
    // 確認した内容から対象が変わっていたらサーバ側で止めてもらう。
    // 変わったまま作ると、運用者が承認していない内容のファイルが銀行に出てしまう。
    ...(dryRun ? {} : { preview_token: preview?.preview_token }),
  });

  // サーバは type / message に加えて、文字不正のときは object に
  // 「どの加盟店の」「どの項目が」問題なのかを構造化して返す。message だけを出すと
  // 運用者は直す対象を特定できないため、必ず明細も表示する。
  const showError = async (error: unknown) => {
    const body = (await Promise.resolve(error).catch(() => null)) as {
      message?: string;
      object?: InvalidField[];
    } | null;
    setErrorMessage(body?.message ?? '振込データの作成に失敗しました');
    setInvalidFields(Array.isArray(body?.object) ? body.object : []);
  };

  const runPreview = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    setInvalidFields([]);
    try {
      setPreview(await createClearingZenginFile(requestParams(true)));
    } catch (error) {
      setPreview(null);
      await showError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const download = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    setInvalidFields([]);
    try {
      const result = await createClearingZenginFile(requestParams(false));
      // 指紋が一致した = 確認した内容のまま作られた。
      // location.href を書き換えると、ファイルがブラウザで開かれて管理画面から離脱してしまう
      // （全銀ファイルは text/plain で配信されるためインライン表示になる）。別タブで開く。
      if (result.url) openInNewTab(result.url);
      close();
    } catch (error) {
      await showError(error);
      // 対象が変わっていた場合はファイルもバッチも作られていない。
      // 古い内容のまま再送させないよう、確認からやり直してもらう。
      setPreview(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      shouldCloseOnOverlayClick
      closeTimeoutMS={200}
      ariaHideApp={false}
      style={modalStyles}
    >
      <Headings.h5>振込データを作成</Headings.h5>

      {errorMessage && (
        <ErrorText>
          {errorMessage}
          {invalidFields.length > 0 && (
            <ul>
              {invalidFields.map((invalid) => (
                <li key={`${invalid.organization_code}-${invalid.field}`}>
                  {invalidFieldText(invalid)}
                </li>
              ))}
            </ul>
          )}
        </ErrorText>
      )}

      {!preview && (
        <>
          <Section>
            <Headings.h6>対象のマネー</Headings.h6>
            <MoneyList>
              {privateMoneys.map((money) => (
                <MoneyLabel key={money.id}>
                  <CheckBox
                    noErrors
                    checked={selectedIds.includes(money.id)}
                    setChecked={() => toggleMoney(money.id)}
                  />
                  {money.name}
                </MoneyLabel>
              ))}
            </MoneyList>
          </Section>
          <Section>
            <Headings.h6>振込指定日</Headings.h6>
            <Row>
              <DateTime
                showIcon
                timeFormat={false}
                defaultValue={transferDate}
                onChange={(date) => setTransferDate(date ?? moment())}
              />
            </Row>
          </Section>
          <Footer>
            <Button variant="neutral" onClick={close}>
              キャンセル
            </Button>
            <Button disabled={selectedIds.length === 0 || isLoading} onClick={runPreview}>
              {isLoading ? '確認中...' : '内容を確認'}
            </Button>
          </Footer>
        </>
      )}

      {preview && (
        <>
          <Section>
            <Summary>
              <div>
                <div>振込件数</div>
                <SummaryValue>{preview.count} 件</SummaryValue>
              </div>
              <div>
                <div>振込金額</div>
                <SummaryValue>{yen(preview.total_amount)}</SummaryValue>
              </div>
              <div>
                <div>振込指定日</div>
                <SummaryValue>{transferDate.format('YYYY/MM/DD')}</SummaryValue>
              </div>
            </Summary>
          </Section>

          {preview.excluded.length > 0 && (
            <Section>
              <Notice level="error">
                口座情報が未登録のため {preview.excluded.length} 件を振込データから除外しました。
                加盟店の口座情報を登録すると次回から含まれます。
                <ul>
                  {withOverflowNote(preview.excluded.map(excludedText)).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </Notice>
            </Section>
          )}

          {preview.warnings.length > 0 && (
            <Section>
              <Notice level="warning">
                <ul>
                  {warningLines(preview.warnings).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </Notice>
            </Section>
          )}

          <Section>
            <Headings.h6>振込内容</Headings.h6>
            <Table>
              <thead>
                <tr>
                  <th>加盟店</th>
                  <th>受取人名</th>
                  <th>金融機関</th>
                  <th>口座</th>
                  <th>内訳</th>
                  <th>金額</th>
                </tr>
              </thead>
              <tbody>
                {preview.transfers.map((transfer) => (
                  // 振込は加盟事業者単位。口座は複数の加盟事業者で重複しうるのでキーにできない。
                  <tr key={transfer.claimed_organization.code}>
                    <td>
                      {transfer.claimed_organization.name}
                      <MerchantCode>{transfer.claimed_organization.code}</MerchantCode>
                    </td>
                    <td>{transfer.bank_account_holder_name}</td>
                    <td>
                      {transfer.bank_name} {transfer.bank_branch_name}
                    </td>
                    <td>{transfer.bank_account}</td>
                    <td>
                      {transfer.breakdown.map((entry) => (
                        <div key={entry.clearing_id}>
                          {entry.private_money?.name ?? ''} {yen(entry.amount)}
                        </div>
                      ))}
                    </td>
                    <td className="amount">{yen(transfer.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>

          <Footer>
            <Button variant="neutral" onClick={() => setPreview(null)}>
              戻る
            </Button>
            <Button disabled={preview.count === 0 || isLoading} onClick={download}>
              {isLoading ? 'ダウンロード中...' : 'ダウンロード'}
            </Button>
          </Footer>
        </>
      )}
    </Modal>
  );
};

export default ZenginExportModal;
